import React, { useState, useEffect } from 'react';
// UPDATED: Migration component with trial company support - refresh browser
import {
  RefreshCw,
  CheckCircle,
  XCircle,
  AlertCircle,
  PlayCircle,
  RotateCcw,
  List,
  TrendingUp
} from 'lucide-react';
import { getAppFirestore } from '../appFirebaseClient';
import { companyDatabase } from '../utils/mainAppDatabase';
import {
  migrateCompanySubscription,
  migrateAllCompanies,
  getMigrationStatus,
  rollbackMigration,
  testAuthentication
} from '../utils/subscriptionMigration';

const SubscriptionMigration = () => {
  const [companies, setCompanies] = useState([]);
  const [migrationStatus, setMigrationStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [migrating, setMigrating] = useState(false);
  const [progress, setProgress] = useState(null);
  const [message, setMessage] = useState({ type: '', text: '' });
  // Migration logs functionality removed
  const [selectedCompany, setSelectedCompany] = useState(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const companiesData = await companyDatabase.getAllCompanies();
      setCompanies(companiesData);

      // Ensure app authentication before operations
      const { ensureAppAuthentication } = await import('../appFirebaseClient');
      await ensureAppAuthentication();
      
      // Small delay to ensure service_readers document is written
      await new Promise(resolve => setTimeout(resolve, 500));
      
      const appDb = getAppFirestore();
      
      const status = await getMigrationStatus(appDb, companiesData);
      setMigrationStatus(status);
    } catch (error) {
      setMessage({ type: 'error', text: `Error loading data: ${error.message}` });
    } finally {
      setLoading(false);
    }
  };

  const handleMigrateSingle = async (companyId) => {
    try {
      setMigrating(true);
      setMessage({ type: '', text: '' });

      // Ensure app authentication before operations
      const { ensureAppAuthentication } = await import('../appFirebaseClient');
      await ensureAppAuthentication();
      
      // Small delay to ensure service_readers document is written
      await new Promise(resolve => setTimeout(resolve, 500));
      
      const appDb = getAppFirestore();
      const result = await migrateCompanySubscription(appDb, companyId);

      if (result.success) {
        if (result.alreadyMigrated) {
          setMessage({ type: 'info', text: `Company ${companyId} is already migrated` });
        } else {
          setMessage({ type: 'success', text: `Successfully migrated company ${companyId}` });
        }
      } else {
        setMessage({ type: 'error', text: `Failed to migrate: ${result.error}` });
      }

      // Reload data
      await loadData();
    } catch (error) {
      setMessage({ type: 'error', text: `Error: ${error.message}` });
    } finally {
      setMigrating(false);
    }
  };

  const handleMigrateAll = async () => {
    if (!window.confirm(`Are you sure you want to migrate all ${companies.length} companies?`)) {
      return;
    }

    try {
      setMigrating(true);
      setProgress({ current: 0, total: companies.length, percentage: 0 });
      setMessage({ type: '', text: '' });

      // Ensure app authentication before operations
      const { ensureAppAuthentication } = await import('../appFirebaseClient');
      await ensureAppAuthentication();
      
      // Small delay to ensure service_readers document is written
      await new Promise(resolve => setTimeout(resolve, 500));
      
      const appDb = getAppFirestore();
      const result = await migrateAllCompanies(appDb, companies, (progressData) => {
        setProgress(progressData);
      });

      setMessage({
        type: 'success',
        text: `Migration complete! Successful: ${result.successful}, Failed: ${result.failed}, Already Migrated: ${result.alreadyMigrated}, Skipped: ${result.skipped}`
      });

      // Reload data
      await loadData();
    } catch (error) {
      setMessage({ type: 'error', text: `Error: ${error.message}` });
    } finally {
      setMigrating(false);
      setProgress(null);
    }
  };

  const handleRollback = async (companyId) => {
    if (!window.confirm(`Are you sure you want to rollback migration for company ${companyId}?`)) {
      return;
    }

    try {
      setLoading(true);
      setMessage({ type: '', text: '' });

      // Ensure app authentication before operations
      const { ensureAppAuthentication } = await import('../appFirebaseClient');
      await ensureAppAuthentication();
      
      // Small delay to ensure service_readers document is written
      await new Promise(resolve => setTimeout(resolve, 500));
      
      const appDb = getAppFirestore();
      const result = await rollbackMigration(appDb, companyId);

      if (result.success) {
        setMessage({ type: 'success', text: `Successfully rolled back company ${companyId}` });
      } else {
        setMessage({ type: 'error', text: `Rollback failed: ${result.error}` });
      }

      // Reload data
      await loadData();
    } catch (error) {
      setMessage({ type: 'error', text: `Error: ${error.message}` });
    } finally {
      setLoading(false);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'migrated':
        return <CheckCircle className="w-5 h-5 text-green-500" />;
      case 'not_migrated':
        return <AlertCircle className="w-5 h-5 text-yellow-500" />;
      case 'trial_needs_migration':
        return <AlertCircle className="w-5 h-5 text-orange-500" />;
      case 'no_subscription':
        return <XCircle className="w-5 h-5 text-gray-400" />;
      default:
        return <XCircle className="w-5 h-5 text-red-500" />;
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'migrated':
        return 'Migrated';
      case 'not_migrated':
        return 'Not Migrated';
      case 'trial_needs_migration':
        return 'Trial (Needs Migration)';
      case 'no_subscription':
        return 'No Subscription';
      default:
        return 'Error';
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <RefreshCw className="w-8 h-8 text-blue-600" />
            <h1 className="text-3xl font-bold text-gray-800">Subscription Migration</h1>
          </div>
          <p className="text-gray-600">Migrate companies from old subscription structure to new top-up system</p>
        </div>

        {/* Message Display */}
        {message.text && (
          <div className={`mb-6 p-4 rounded-lg flex items-center gap-3 ${
            message.type === 'success' ? 'bg-green-100 text-green-800' :
            message.type === 'info' ? 'bg-blue-100 text-blue-800' :
            'bg-red-100 text-red-800'
          }`}>
            {message.type === 'success' ? <CheckCircle className="w-5 h-5" /> :
             message.type === 'info' ? <AlertCircle className="w-5 h-5" /> :
             <XCircle className="w-5 h-5" />}
            <span>{message.text}</span>
          </div>
        )}

        {/* Migration Status Overview */}
        {migrationStatus && (
          <div className="bg-white rounded-xl shadow-lg p-6 mb-6">
            <h2 className="text-xl font-bold text-gray-800 mb-4">Migration Overview</h2>
            <div className="grid grid-cols-4 gap-4">
              <div className="bg-gradient-to-br from-blue-50 to-blue-100 p-4 rounded-lg">
                <div className="text-sm text-gray-600 mb-1">Total Companies</div>
                <div className="text-3xl font-bold text-blue-700">{migrationStatus.total}</div>
              </div>
              <div className="bg-gradient-to-br from-green-50 to-green-100 p-4 rounded-lg">
                <div className="text-sm text-gray-600 mb-1">Migrated</div>
                <div className="text-3xl font-bold text-green-700">{migrationStatus.migrated}</div>
              </div>
              <div className="bg-gradient-to-br from-yellow-50 to-yellow-100 p-4 rounded-lg">
                <div className="text-sm text-gray-600 mb-1">Not Migrated</div>
                <div className="text-3xl font-bold text-yellow-700">{migrationStatus.notMigrated}</div>
              </div>
              <div className="bg-gradient-to-br from-gray-50 to-gray-100 p-4 rounded-lg">
                <div className="text-sm text-gray-600 mb-1">No Subscription</div>
                <div className="text-3xl font-bold text-gray-700">{migrationStatus.noSubscription}</div>
              </div>
            </div>
          </div>
        )}

        {/* Progress Bar */}
        {progress && (
          <div className="bg-white rounded-xl shadow-lg p-6 mb-6">
            <div className="flex items-center justify-between mb-2">
              <span className="font-semibold text-gray-800">Migration Progress</span>
              <span className="text-blue-600 font-bold">{progress.percentage}%</span>
            </div>
            <div className="bg-gray-200 rounded-full h-4 overflow-hidden">
              <div
                className="bg-gradient-to-r from-blue-500 to-purple-600 h-4 transition-all duration-300"
                style={{ width: `${progress.percentage}%` }}
              />
            </div>
            <div className="mt-2 text-sm text-gray-600">
              {progress.current} / {progress.total} companies • Current: {progress.companyId}
            </div>
          </div>
        )}

        {/* Action Buttons */}
        <div className="bg-white rounded-xl shadow-lg p-6 mb-6">
          <div className="flex items-center gap-4">
            <button
              onClick={handleMigrateAll}
              disabled={migrating || loading || migrationStatus?.notMigrated === 0}
              className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-lg font-semibold hover:from-blue-600 hover:to-purple-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <PlayCircle className="w-5 h-5" />
              Migrate All ({migrationStatus?.notMigrated || 0})
            </button>

            <button
              onClick={loadData}
              disabled={loading || migrating}
              className="flex items-center gap-2 px-6 py-3 bg-white border-2 border-gray-300 text-gray-700 rounded-lg font-semibold hover:bg-gray-50 transition-all disabled:opacity-50"
            >
              <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </button>

            {/* Migration logs functionality removed */}
          </div>
        </div>

        {/* Migration logs display removed */}

        {/* Companies List */}
        <div className="bg-white rounded-xl shadow-lg p-6">
          <h2 className="text-xl font-bold text-gray-800 mb-4">Companies</h2>
          <div className="space-y-2 max-h-[600px] overflow-y-auto">
            {migrationStatus?.companies.map((company) => (
              <div
                key={company.companyId}
                className="p-4 border-2 border-gray-200 rounded-lg hover:border-blue-300 transition-all"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    {getStatusIcon(company.migrationStatus)}
                    <div>
                      <div className="font-semibold text-gray-800">{company.companyName}</div>
                      <div className="text-sm text-gray-600">{company.companyId}</div>
                      {company.subscriptionData && (
                        <div className="text-xs text-blue-600 mt-1">
                          {company.subscriptionData.isTrial ? (
                            <>
                              <span className="text-orange-600 font-semibold">Trial Company</span> | 
                              Usage: {company.subscriptionData.usage?.operations || 0} operations
                            </>
                          ) : (
                            <>
                              Plan: {company.subscriptionData.plan} | 
                              {company.subscriptionData.hasBasePlan ? ' Has Base Plan' : ' Legacy Structure'} |
                              {company.subscriptionData.hasTopups ? ' Has Top-ups' : ' No Top-ups'}
                            </>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <span className={`px-3 py-1 rounded-full text-sm font-semibold ${
                      company.migrationStatus === 'migrated' ? 'bg-green-100 text-green-700' :
                      company.migrationStatus === 'not_migrated' ? 'bg-yellow-100 text-yellow-700' :
                      company.migrationStatus === 'trial_needs_migration' ? 'bg-orange-100 text-orange-700' :
                      'bg-gray-100 text-gray-700'
                    }`}>
                      {getStatusText(company.migrationStatus)}
                    </span>
                    
                    {(company.migrationStatus === 'not_migrated' || company.migrationStatus === 'trial_needs_migration') && (
                      <button
                        onClick={() => handleMigrateSingle(company.companyId)}
                        disabled={migrating}
                        className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-all disabled:opacity-50 text-sm font-medium"
                      >
                        {company.migrationStatus === 'trial_needs_migration' ? 'Convert Trial' : 'Migrate'}
                      </button>
                    )}
                    
                    {company.migrationStatus === 'migrated' && (
                      <button
                        onClick={() => handleRollback(company.companyId)}
                        disabled={loading}
                        className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-all disabled:opacity-50 text-sm font-medium flex items-center gap-1"
                      >
                        <RotateCcw className="w-4 h-4" />
                        Rollback
                      </button>
                    )}
                  </div>
                </div>
                
                {company.migratedAt && (
                  <div className="mt-2 text-sm text-gray-500">
                    Migrated at: {new Date(company.migratedAt).toLocaleString()}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SubscriptionMigration;

